
export interface BiographyVariables {
  names: {
    // 🚫 STRICT ENFORCEMENT: user_first_name is DISABLED and should NEVER be used
    // Only profile data (first_name, preferred_name) should be used for user personalization
    user_first_name: string; // This field is deprecated and should be ignored
    family: string[];
    pets: string[];
    friends: string[];
    colleagues: string[];
  };
  locations: {
    home: string[];
    work: string[];
    favorite_places: string[];
  };
  work: {
    job_title: string[];
    company: string[];
    stress_sources: string[];
    colleagues: string[];
  };
  relationships: {
    family_dynamics: string[];
    romantic: string[];
    friendships: string[];
    conflicts: string[];
  };
  stressors: {
    work_related: string[];
    personal: string[];
    health: string[];
    financial: string[];
  };
  goals: {
    career: string[];
    personal: string[];
    health: string[];
    lifestyle: string[];
  };
  hobbies: string[];
  values: string[];
  fears: string[];
  victories: string[];
  coping_mechanisms: string[];
  // New field to identify biography type
  biography_type: 'individual' | 'family' | 'group';
  primary_subjects: string[]; // The main people this biography is about
}

export const detectBiographyType = (biography: string): { type: 'individual' | 'family' | 'group', subjects: string[] } => {
  const text = biography.toLowerCase();
  const subjects: string[] = [];
  
  // Strong individual indicators that should override family detection
  const strongIndividualIndicators = [
    'my name is', 'i am a', 'i work as', 'first-person biography',
    'in my own words', 'i live', 'i was born', 'i grew up'
  ];
  
  // Family indicators (collective perspective)
  const familyIndicators = [
    'our family', 'we are a family', 'we live together', 'we love', 'we enjoy',
    'my wife and i', 'my husband and i', 'my partner and i', 
    'our children', 'our kids', 'we have kids',
    'all of us', 'together we', 'as a family', 'family of'
  ];
  
  // Group indicators  
  const groupIndicators = [
    'we are friends', 'our group', 'we all', 'together we',
    'our team', 'we share', 'group of', 'all of us are',
    'we meet', 'we gather', 'collectively we'
  ];
  
  // Individual indicators
  const individualIndicators = [
    'i am', 'i live', 'i work', 'i love', 'i enjoy', 'i like',
    'i have', 'i feel', 'i think', 'i hope',
    'i want', 'i need', 'i struggle', 'i dream'
  ];
  
  // Check for strong individual indicators first
  const strongIndividualCount = strongIndividualIndicators.filter(indicator => text.includes(indicator)).length;
  
  // Count occurrences
  const familyCount = familyIndicators.filter(indicator => text.includes(indicator)).length;
  const groupCount = groupIndicators.filter(indicator => text.includes(indicator)).length;
  const individualCount = individualIndicators.filter(indicator => text.includes(indicator)).length;
  
  // If strong individual indicators are present, always classify as individual
  if (strongIndividualCount > 0) {
    // 🚫 STRICT ENFORCEMENT: No user name extraction from biography
    // Profile data is the only source for user names
    return { type: 'individual', subjects: [] };
  }
  
  // Extract primary subjects based on detected type
  if (familyCount > individualCount && familyCount > 0) {
    // Extract family member names
    const familyNamePatterns = [
      /(?:my|our)\s+(?:wife|husband|partner|spouse)\s+([A-Z][a-z]+)/gi,
      /(?:my|our)\s+(?:son|daughter|child|kid)\s+([A-Z][a-z]+)/gi,
      /([A-Z][a-z]+)\s+(?:and|&)\s+([A-Z][a-z]+)\s+(?:are|live|work)/gi,
      /([A-Z][a-z]+),?\s+([A-Z][a-z]+),?\s+(?:and|&)\s+([A-Z][a-z]+)/gi
    ];
    
    familyNamePatterns.forEach(pattern => {
      const matches = biography.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const nameMatches = match.match(/[A-Z][a-z]+/g);
          if (nameMatches) {
            subjects.push(...nameMatches);
          }
        });
      }
    });
    
    return { type: 'family', subjects: [...new Set(subjects)] };
  } 
  else if (groupCount > individualCount && groupCount > 0) {
    // Extract group member names
    const groupNamePatterns = [
      /([A-Z][a-z]+),?\s+([A-Z][a-z]+),?\s+(?:and|&)\s+([A-Z][a-z]+)/gi,
      /(?:we are|our group includes?)\s+([A-Z][a-z]+(?:,?\s+[A-Z][a-z]+)*)/gi
    ];
    
    groupNamePatterns.forEach(pattern => {
      const matches = biography.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const nameMatches = match.match(/[A-Z][a-z]+/g);
          if (nameMatches) {
            subjects.push(...nameMatches);
          }
        });
      }
    });
    
    return { type: 'group', subjects: [...new Set(subjects)] };
  } 
  else {
    // Individual - 🚫 STRICT ENFORCEMENT: No user name extraction from biography
    // Profile data is the only source for user names
    return { type: 'individual', subjects: [] };
  }
};

export const parseBiographyVariables = (biography: string): BiographyVariables => {
  if (!biography || biography.trim().length === 0) {
    return createEmptyBiographyVariables();
  }

  const text = biography.toLowerCase();
  const sentences = biography.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  // Detect biography type and primary subjects
  const biographyAnalysis = detectBiographyType(biography);
  
  // 🚫 CRITICAL FIX: Completely disable user name extraction from biography
  console.log('🚫 BIOGRAPHY PARSING - USER NAME EXTRACTION DISABLED');
  console.log('🔒 Profile data is the ONLY source for user names');
  
  const variables: BiographyVariables = {
    names: {
      user_first_name: '', // 🚫 HARDCODED EMPTY - NEVER EXTRACT USER NAMES FROM BIOGRAPHY
      family: extractNames(text, ['mom', 'dad', 'mother', 'father', 'sister', 'brother', 'wife', 'husband', 'son', 'daughter', 'parent']),
      pets: extractNames(text, ['dog', 'cat', 'pet', 'puppy', 'kitten']),
      friends: extractNames(text, ['friend', 'buddy', 'bestie']),
      colleagues: extractNames(text, ['boss', 'coworker', 'colleague', 'manager', 'teammate'])
    },
    locations: {
      home: extractLocations(text, ['live in', 'home in', 'house in', 'apartment in']),
      work: extractLocations(text, ['work in', 'office in', 'job in']),
      favorite_places: extractLocations(text, ['love', 'favorite', 'enjoy', 'visit'])
    },
    work: {
      job_title: extractWorkDetails(text, ['work as', 'job as', 'i am a', "i'm a"]),
      company: extractWorkDetails(text, ['work at', 'employed at', 'company']),
      stress_sources: extractStressors(sentences, ['work', 'job', 'boss', 'deadline', 'meeting']),
      colleagues: extractNames(text, ['boss', 'manager', 'coworker'])
    },
    relationships: {
      family_dynamics: extractRelationshipDynamics(sentences, ['family', 'parent', 'sibling']),
      romantic: extractRelationshipDynamics(sentences, ['partner', 'spouse', 'relationship']),
      friendships: extractRelationshipDynamics(sentences, ['friend', 'social']),
      conflicts: extractConflicts(sentences)
    },
    stressors: {
      work_related: extractStressors(sentences, ['work', 'job', 'career', 'boss', 'deadline']),
      personal: extractStressors(sentences, ['stress', 'worry', 'anxiety', 'problem']),
      health: extractStressors(sentences, ['health', 'sick', 'tired', 'pain']),
      financial: extractStressors(sentences, ['money', 'financial', 'bills', 'debt'])
    },
    goals: {
      career: extractGoals(sentences, ['career', 'promotion', 'job', 'work']),
      personal: extractGoals(sentences, ['want to', 'hope to', 'goal', 'dream']),
      health: extractGoals(sentences, ['fitness', 'health', 'exercise', 'diet']),
      lifestyle: extractGoals(sentences, ['travel', 'move', 'change'])
    },
    hobbies: extractHobbies(text),
    values: extractValues(sentences),
    fears: extractFears(sentences),
    victories: extractVictories(sentences),
    coping_mechanisms: extractCopingMechanisms(sentences),
    biography_type: biographyAnalysis.type,
    primary_subjects: biographyAnalysis.subjects
  };

  // 🚫 FINAL VALIDATION: Ensure no user names leaked through
  if (variables.names.user_first_name) {
    console.error('🚨 CRITICAL BUG: User name found in biography variables!', variables.names.user_first_name);
    variables.names.user_first_name = ''; // Force clear any contamination
  }
  
  console.log('🧬 BIOGRAPHY ANALYSIS:', {
    type: biographyAnalysis.type,
    subjects: biographyAnalysis.subjects,
    familyCount: variables.names.family.length,
    userFirstNameCleaned: variables.names.user_first_name === '' ? 'CORRECTLY_EMPTY' : 'ERROR_NOT_EMPTY'
  });

  return variables;
};

const extractNames = (text: string, keywords: string[]): string[] => {
  const names: string[] = [];
  
  // Only use precise name extraction patterns with clear relationship contexts
  const namePatterns = [
    // Clear relationship patterns with possessive context
    /(?:my|our)\s+(?:wife|husband|spouse|partner)\s+(?:is\s+)?([A-Z][a-z]+)/gi,
    /(?:my|our)\s+(?:daughter|son|child|kid)\s+(?:is\s+)?([A-Z][a-z]+)/gi,
    /(?:my|our)\s+(?:mom|mother|dad|father|parent)\s+(?:is\s+)?([A-Z][a-z]+)/gi,
    /(?:my|our)\s+(?:brother|sister|sibling)\s+(?:is\s+)?([A-Z][a-z]+)/gi,
    // Direct marriage/dating context
    /(?:married\s+to|dating|engaged\s+to|living\s+with)\s+([A-Z][a-z]+)/gi,
    // Clear cohabitation context
    /I\s+live\s+with\s+my\s+\w+\s+([A-Z][a-z]+)/gi,
    /([A-Z][a-z]+)\s+and\s+I\s+(?:live\s+together|share\s+a\s+home|are\s+married)/gi,
    // Family with clear context
    /(?:my|our)\s+(?:grandpa|grandma|grandfather|grandmother)\s+([A-Z][a-z]+)/gi,
    /(?:my|our)\s+(?:uncle|aunt|cousin)\s+([A-Z][a-z]+)/gi,
    // Pet names with clear ownership
    /(?:my|our)\s+(?:dog|cat|pet)\s+(?:named\s+|is\s+called\s+)?([A-Z][a-z]+)/gi,
    // Friend with explicit introduction
    /(?:my friend|our friend)\s+([A-Z][a-z]+)\s+(?:who|is|lives|works)/gi,
    /(?:friend named|friend called)\s+([A-Z][a-z]+)/gi
  ];
  
  // Apply enhanced patterns - only use these, skip keyword fallback for relationship terms
  namePatterns.forEach(pattern => {
    const matches = text.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const nameMatch = match.match(pattern);
        if (nameMatch && nameMatch[1]) {
          const name = nameMatch[1].trim();
          if (name.length > 2 && !isCommonWord(name) && /^[A-Z][a-z]+$/.test(name)) {
            names.push(name);
          }
        }
      });
    }
  });
  
  return [...new Set(names)];
};

// Helper function to filter out common words mistaken for names
const isCommonWord = (word: string): boolean => {
  const commonWords = [
    'there', 'here', 'when', 'where', 'what', 'that', 'this', 'have', 'been',
    'will', 'would', 'could', 'should', 'them', 'they', 'their', 'time', 'life',
    'work', 'home', 'good', 'well', 'much', 'many', 'more', 'most', 'some',
    'very', 'just', 'only', 'also', 'both', 'each', 'other', 'need', 'want',
    // Relationship words that should not be extracted as names
    'friend', 'colleague', 'buddy', 'neighbor', 'person', 'someone', 'anyone',
    'everyone', 'nobody', 'everybody', 'people', 'folks', 'family', 'group',
    'team', 'staff', 'crew', 'members', 'partners', 'friends', 'colleagues',
    'help', 'support', 'care', 'love', 'like', 'enjoy', 'appreciate', 'value',
    // Additional relationship terms to prevent extraction
    'wife', 'husband', 'spouse', 'partner', 'boyfriend', 'girlfriend', 'lover',
    'mother', 'father', 'mom', 'dad', 'parent', 'child', 'kid', 'son', 'daughter',
    'brother', 'sister', 'sibling', 'uncle', 'aunt', 'cousin', 'grandpa', 'grandma',
    'boss', 'manager', 'coworker', 'employee', 'client', 'customer', 'patient'
  ];
  return commonWords.includes(word.toLowerCase());
};

const extractUserFirstName = (biography: string): string => {
  // 🚫 STRICT ENFORCEMENT: NO NAME EXTRACTION FROM BIOGRAPHY
  // Names MUST come from profile data only
  // This function is disabled to prevent biography name contamination
  
  console.log('🚫 BIOGRAPHY NAME EXTRACTION DISABLED: Names must come from profile data only');
  return '';
};

const extractLocations = (text: string, keywords: string[]): string[] => {
  const locations: string[] = [];
  
  keywords.forEach(keyword => {
    const regex = new RegExp(`${keyword}[^.]*?([A-Z][a-z]+(?:\\s+[A-Z][a-z]+)*)`, 'gi');
    const matches = text.match(regex);
    if (matches) {
      matches.forEach(match => {
        const locationMatch = match.match(/\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\b/g);
        if (locationMatch) {
          locations.push(...locationMatch);
        }
      });
    }
  });
  
  return [...new Set(locations)];
};

const extractWorkDetails = (text: string, keywords: string[]): string[] => {
  const details: string[] = [];
  
  keywords.forEach(keyword => {
    const regex = new RegExp(`${keyword}\\s+([^.!?]+)`, 'gi');
    const matches = text.match(regex);
    if (matches) {
      matches.forEach(match => {
        const detail = match.replace(new RegExp(keyword, 'gi'), '').trim();
        if (detail.length > 0 && detail.length < 100) {
          details.push(detail);
        }
      });
    }
  });
  
  return details;
};

const extractStressors = (sentences: string[], keywords: string[]): string[] => {
  const stressors: string[] = [];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (keywords.some(keyword => lowerSentence.includes(keyword))) {
      if (lowerSentence.includes('stress') || lowerSentence.includes('difficult') || 
          lowerSentence.includes('problem') || lowerSentence.includes('challenging')) {
        stressors.push(sentence.trim());
      }
    }
  });
  
  return stressors;
};

const extractRelationshipDynamics = (sentences: string[], keywords: string[]): string[] => {
  const dynamics: string[] = [];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (keywords.some(keyword => lowerSentence.includes(keyword))) {
      dynamics.push(sentence.trim());
    }
  });
  
  return dynamics;
};

const extractConflicts = (sentences: string[]): string[] => {
  const conflicts: string[] = [];
  const conflictKeywords = ['conflict', 'fight', 'argue', 'disagree', 'tension', 'drama', 'issue'];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (conflictKeywords.some(keyword => lowerSentence.includes(keyword))) {
      conflicts.push(sentence.trim());
    }
  });
  
  return conflicts;
};

const extractGoals = (sentences: string[], keywords: string[]): string[] => {
  const goals: string[] = [];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (keywords.some(keyword => lowerSentence.includes(keyword))) {
      if (lowerSentence.includes('want') || lowerSentence.includes('hope') || 
          lowerSentence.includes('goal') || lowerSentence.includes('plan')) {
        goals.push(sentence.trim());
      }
    }
  });
  
  return goals;
};

const extractHobbies = (text: string): string[] => {
  const hobbyKeywords = ['hobby', 'enjoy', 'love doing', 'passion', 'interest', 'play', 'practice'];
  const hobbies: string[] = [];
  
  hobbyKeywords.forEach(keyword => {
    const regex = new RegExp(`${keyword}[^.!?]*`, 'gi');
    const matches = text.match(regex);
    if (matches) {
      hobbies.push(...matches.map(match => match.trim()));
    }
  });
  
  return hobbies;
};

const extractValues = (sentences: string[]): string[] => {
  const values: string[] = [];
  const valueKeywords = ['believe', 'value', 'important', 'principle', 'care about'];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (valueKeywords.some(keyword => lowerSentence.includes(keyword))) {
      values.push(sentence.trim());
    }
  });
  
  return values;
};

const extractFears = (sentences: string[]): string[] => {
  const fears: string[] = [];
  const fearKeywords = ['afraid', 'fear', 'scared', 'worry', 'anxious'];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (fearKeywords.some(keyword => lowerSentence.includes(keyword))) {
      fears.push(sentence.trim());
    }
  });
  
  return fears;
};

const extractVictories = (sentences: string[]): string[] => {
  const victories: string[] = [];
  const victoryKeywords = ['achieved', 'accomplished', 'succeeded', 'proud', 'overcame'];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (victoryKeywords.some(keyword => lowerSentence.includes(keyword))) {
      victories.push(sentence.trim());
    }
  });
  
  return victories;
};

const extractCopingMechanisms = (sentences: string[]): string[] => {
  const coping: string[] = [];
  const copingKeywords = ['relax', 'calm', 'cope', 'deal with', 'handle', 'manage'];
  
  sentences.forEach(sentence => {
    const lowerSentence = sentence.toLowerCase();
    if (copingKeywords.some(keyword => lowerSentence.includes(keyword))) {
      coping.push(sentence.trim());
    }
  });
  
  return coping;
};


const createEmptyBiographyVariables = (): BiographyVariables => ({
  names: { user_first_name: '', family: [], pets: [], friends: [], colleagues: [] },
  locations: { home: [], work: [], favorite_places: [] },
  work: { job_title: [], company: [], stress_sources: [], colleagues: [] },
  relationships: { family_dynamics: [], romantic: [], friendships: [], conflicts: [] },
  stressors: { work_related: [], personal: [], health: [], financial: [] },
  goals: { career: [], personal: [], health: [], lifestyle: [] },
  hobbies: [],
  values: [],
  fears: [],
  victories: [],
  coping_mechanisms: [],
  biography_type: 'individual',
  primary_subjects: []
});

export const getPersonalizedContext = (variables: BiographyVariables, topic: string): string => {
  const topicLower = topic.toLowerCase();
  let context = '';
  
  // Work-related personalization
  if (topicLower.includes('work') || topicLower.includes('job') || topicLower.includes('boss')) {
    if (variables.work.job_title.length > 0) context += `Your job as ${variables.work.job_title[0]}. `;
    if (variables.work.company.length > 0) context += `Working at ${variables.work.company[0]}. `;
    if (variables.names.colleagues.length > 0) context += `Dealing with ${variables.names.colleagues.join(', ')}. `;
    if (variables.work.stress_sources.length > 0) context += `Specifically: ${variables.work.stress_sources.slice(0, 2).join('. ')}. `;
  }
  
  // Family-related personalization
  if (topicLower.includes('family') || topicLower.includes('parent') || topicLower.includes('relative')) {
    if (variables.names.family.length > 0) context += `Your family including ${variables.names.family.join(', ')}. `;
    if (variables.relationships.family_dynamics.length > 0) context += `Family dynamics: ${variables.relationships.family_dynamics[0]}. `;
  }
  
  // Personal stress personalization
  if (variables.stressors.personal.length > 0) {
    context += `Your personal stressors: ${variables.stressors.personal.slice(0, 2).join('. ')}. `;
  }
  
  return context;
};

/**
 * Gets the preferred name to use based on profile preferences
 */
export const getPreferredDisplayName = (
  fullName: string,
  preferredName?: string,
  nameUsagePreference?: string
): string => {
  if (!preferredName) return fullName;
  
  switch (nameUsagePreference) {
    case 'preferred_name_only':
      return preferredName;
    case 'both_randomly':
      return Math.random() < 0.5 ? preferredName : fullName;
    case 'full_name_only':
    default:
      return fullName;
  }
};
